import React, { useState, useEffect } from "react";
import api from "../api/api";
import { formatDateDDMMYYYY } from "../utils/dateFormat";
import { getTotalsByColumn, isTotalsTable } from "../utils/tableTotals";
import "../page/SummaryRev.css";

const BoeDetailView = ({ boeNo, type, onTypeChange }) => {
  const [activeTab, setActiveTab] = useState("moowr");
  const [data, setData] = useState(null);
  const [summaryTabData, setSummaryTabData] = useState({
    summary: null,
    sales: null,
    scrap: null,
    wip: null,
    moowr: null,
    rmBoe: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fullscreenTable, setFullscreenTable] = useState(null);
  const [fontScale, setFontScale] = useState(100);

  useEffect(() => {
    if (boeNo && type) {
      if (type.toLowerCase().includes("summary")) {
        setSummaryTabData({
          summary: null,
          sales: null,
          scrap: null,
          wip: null,
          moowr: null,
          rmBoe: null,
        });
        setData(null);
        setActiveTab("moowr");
        fetchSummaryTabData("summary");
        fetchSummaryTabData("moowr");
      } else {
        fetchBoeData(type);
      }
    }
  }, [boeNo, type]);

  useEffect(() => {
    if (!boeNo || !type || !type.toLowerCase().includes("summary")) return;
    if (summaryTabData[activeTab]) return;
    fetchSummaryTabData(activeTab);
  }, [activeTab, boeNo, type]);

  useEffect(() => {
    if (typeof onTypeChange !== "function") return;

    const baseType = (type || "").toLowerCase();
    if (baseType.includes("summary")) {
      const tabTypeMap = {
        sales: "Sales by BOE No",
        scrap: "Scrap BY BOE",
        wip: "WIP By BOE",
        moowr: "MOOWR Purchase",
        rmBoe: "RM By BOE",
      };
      onTypeChange(tabTypeMap[activeTab] || "MOOWR Purchase");
      return;
    }

    onTypeChange(type);
  }, [activeTab, type, onTypeChange]);

  const normalizeResponse = (response) => response?.BoEDatasWithBoeNo || response;

  const fetchBoeData = async (requestType = type) => {
    setLoading(true);
    setError(null);
    try {
      const userName = sessionStorage.getItem("userName");
      const response = await api.GetBoEDatasWithBoeNo(userName, requestType, boeNo);
      const boeData = normalizeResponse(response);
      setData(boeData);
      setActiveTab("summary");
    } catch (err) {
      console.error("Error fetching BOE data:", err);
      setError("Failed to load BOE details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSummaryTabData = async (tabId) => {
    const tabTypeMap = {
      summary: "Summary Rev",
      sales: "Sales by MCNo",
      scrap: "Scrap BY BOE",
      wip: "WIP By BOE",
      moowr: "MOOWR Purchase",
      rmBoe: "RM By BOE",
    };

    const requestType = tabTypeMap[tabId] || "Summary Rev";
    setLoading(true);
    setError(null);

    try {
      const userName = sessionStorage.getItem("userName");
      const response = await api.GetBoEDatasWithBoeNo(userName, requestType, boeNo);
      const normalized = normalizeResponse(response);
      setSummaryTabData((prev) => ({ ...prev, [tabId]: normalized }));
    } catch (err) {
      console.error("Error fetching summary tab data:", err);
      setError("Failed to load BOE details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return formatDateDDMMYYYY(dateString);
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "number") return value.toLocaleString("en-IN", { maximumFractionDigits: 2 });
    return value;
  };

  const isNumericValue = (value) => {
    if (typeof value === "number") return Number.isFinite(value);
    if (typeof value !== "string") return false;
    const normalized = value.trim().replace(/,/g, "");
    return /^-?\d+(\.\d+)?$/.test(normalized);
  };

  const isColumnConfigTable = (table) => {
    return (
      Array.isArray(table) &&
      table.length > 0 &&
      ["DbColDisplay", "DisplayColName", "Visible", "Priority"].every((key) => key in table[0])
    );
  };

  const getOrderedColumns = (tableData, columnConfig) => {
    const defaultColumns = Object.keys(tableData[0] || {});
    if (!columnConfig) return defaultColumns;
    const normalizeLabel = (value) =>
      String(value || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

    const configuredColumns = new Set(
      columnConfig
        .filter((cfg) => cfg.DbColDisplay)
        .map((cfg) => cfg.DbColDisplay)
    );

    const visibleConfigs = columnConfig
      .filter((cfg) => Number(cfg.Visible) !== 0 && cfg.DbColDisplay)
      .sort((a, b) => Number(a.Priority || 9999) - Number(b.Priority || 9999));

    // Avoid duplicate headings (for example two columns both named "BOE No").
    // Keep the configured column that has row data when duplicates exist.
    const selectedByDisplay = new Map();
    visibleConfigs.forEach((cfg) => {
      const displayKey = normalizeLabel(cfg.DisplayColName || cfg.DbColDisplay);
      const hasDataColumn = defaultColumns.includes(cfg.DbColDisplay);
      const existing = selectedByDisplay.get(displayKey);

      if (!existing) {
        selectedByDisplay.set(displayKey, { cfg, hasDataColumn });
        return;
      }

      if (!existing.hasDataColumn && hasDataColumn) {
        selectedByDisplay.set(displayKey, { cfg, hasDataColumn: true });
      }
    });

    const configColumns = visibleConfigs
      .filter((cfg) => {
        const displayKey = normalizeLabel(cfg.DisplayColName || cfg.DbColDisplay);
        return selectedByDisplay.get(displayKey)?.cfg?.DbColDisplay === cfg.DbColDisplay;
      })
      .map((cfg) => cfg.DbColDisplay);

    const remainingColumns = defaultColumns.filter((col) => !configuredColumns.has(col));
    return [...configColumns, ...remainingColumns];
  };

  const getDisplayNameMap = (columnConfig) => {
    if (!columnConfig) return {};
    return columnConfig.reduce((acc, cfg) => {
      if (cfg.DbColDisplay) acc[cfg.DbColDisplay] = cfg.DisplayColName || cfg.DbColDisplay;
      return acc;
    }, {});
  };

  const renderRecord = (record, title = "Summary") => {
    if (!record) return null;

    return (
      <div className="summary-card-record">
        {title && <div className="detail-table-header"><div className="detail-table-title">{title}</div></div>}
        <div className="summary-card-record-grid">
          {Object.entries(record).map(([key, value]) => {
            let displayValue = value;

            if (value === null || value === undefined) {
              displayValue = "-";
            } else if (typeof value === "number") {
              displayValue = formatNumber(value);
            } else if (key.toLowerCase().includes("date")) {
              displayValue = formatDate(value);
            }

            return (
              <div key={key} className="summary-card-entry">
                <span className="summary-card-key">
                  {key.replace(/_/g, " ")}
                </span>
                <span className="summary-card-value">
                  {displayValue}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSummaryHighlights = (record) => {
    if (!record) return null;

    const summaryFields = [
      { key: "Rev_BOE", label: "BOE" },
      { key: "BOE_Date", label: "BOE Date" },
      { key: "Import_Qty_Kgs", label: "Import Qty Kgs" },
      { key: "Import_Assessable_Value", label: "Import Assessable Value" },
      { key: "Total_Shipped_MOOWR", label: "Total Shipped MOOWR" },
      { key: "WIP_in_Hand", label: "WIP in Hand" },
      { key: "Scrap_Sales_BOE", label: "Scrap Sales BOE" },
    ];

    return (
      <div className="detail-highlights">
        <div className="detail-highlights-grid">
          {summaryFields.map(({ key, label }) => {
            const rawValue = record[key];
            let displayValue = rawValue;

            if (rawValue === null || rawValue === undefined) {
              displayValue = "-";
            } else if (typeof rawValue === "number") {
              displayValue = formatNumber(rawValue);
            } else if (key.toLowerCase().includes("date")) {
              displayValue = formatDate(rawValue);
            }

            return (
              <div key={key} className="detail-highlight">
                <div className="detail-highlight-label">{label}</div>
                <div className="detail-highlight-value">{displayValue}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTable = (tableData, title, tableKey, columnConfig = null, options = {}) => {
    const { showHeader = true, allowFullscreen = true, isFullscreen = false, totalsTable = null } = options;

    if (!tableData || tableData.length === 0) {
      return (
        <div className="detail-empty">
          <p>No data available</p>
        </div>
      );
    }

    const columns = getOrderedColumns(tableData, columnConfig);
    const displayNameMap = getDisplayNameMap(columnConfig);
    const headerCellStyle = {
      paddingLeft: "8px",
      paddingRight: "8px",
      paddingTop: "10px",
      paddingBottom: "10px",
      minHeight: "52px",
      lineHeight: "1.2",
    };
    const normalizeColumnLabel = (label) =>
      String(label || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    const isSlNoColumn = (label, columnKey) => {
      const candidates = [label, columnKey]
        .map((item) => normalizeColumnLabel(item))
        .filter(Boolean);

      return candidates.some(
        (normalized) =>
          normalized === "boeslno" ||
          normalized === "slno" ||
          normalized === "sno" ||
          normalized === "serialno" ||
          normalized === "serialnumber" ||
          normalized === "sl"
      );
    };
    const getColumnWidthStyle = (label) => {
      const normalized = normalizeColumnLabel(label);
      if (normalized === "description") {
        return { width: "160px", minWidth: "160px", maxWidth: "160px" };
      }
      if (normalized === "salesbyboeno") {
        return { width: "130px", minWidth: "130px", maxWidth: "130px" };
      }
      return {};
    };
    const shouldWrapContent = (label) => normalizeColumnLabel(label) === "description";
    const numericColumns = new Set(
      columns.filter((col) => tableData.some((row) => isNumericValue(row[col])))
    );
    const totalsByColumn = getTotalsByColumn(totalsTable, columns);

    return (
      <div className={`detail-table-box ${isFullscreen ? "is-fullscreen" : ""}`}>
        {showHeader && title && (
          <div className="detail-table-header">
            <div className="flex items-center gap-3">
              <h3 className="detail-table-title">{title}</h3>
              <span className="detail-table-meta">{tableData.length} rows</span>
            </div>
            <div className="detail-table-actions">
              <button
                type="button"
                onClick={() => setFontScale((prev) => Math.max(80, prev - 10))}
                className="summary-btn"
                title="Decrease Font Size"
              >
                A-
              </button>
              <button
                type="button"
                onClick={() => setFontScale((prev) => Math.min(140, prev + 10))}
                className="summary-btn"
                title="Increase Font Size"
              >
                A+
              </button>
              {allowFullscreen && (
                <button
                  type="button"
                  onClick={() =>
                    setFullscreenTable(
                      fullscreenTable && fullscreenTable.key === tableKey
                        ? null
                        : { key: tableKey, title, tableData, columnConfig, totalsTable }
                    )
                  }
                  className="summary-btn"
                  title={fullscreenTable && fullscreenTable.key === tableKey ? "Minimize" : "Expand"}
                >
                  {fullscreenTable && fullscreenTable.key === tableKey ? "Min" : "Full"}
                </button>
              )}
            </div>
          </div>
        )}
        <div className={`detail-table-wrap ${isFullscreen ? "max-h-[calc(100vh-56px)]" : "max-h-[78vh]"}`}>
          <table className="detail-grid" style={{ fontSize: `${fontScale}%` }}>
            <thead>
              <tr>
                {columns.map((col) => {
                  const displayLabel = displayNameMap[col] || col.replace(/_/g, " ");
                  const isSlNo = isSlNoColumn(displayLabel, col);
                  const totalValue = totalsByColumn[col];
                  return (
                    <th
                      key={col}
                      className={`whitespace-normal break-words align-middle ${isSlNo ? "text-center" : numericColumns.has(col) ? "text-right" : "text-left"
                        }`}
                      style={{ ...headerCellStyle, ...getColumnWidthStyle(displayLabel) }}
                    >
                      <div className="flex flex-col">
                        <span className="leading-tight">{displayLabel}</span>
                        <span
                          className={`text-sm font-medium leading-tight min-h-[18px] ${totalValue !== undefined ? "text-emerald-700" : "text-transparent"
                            }`}
                        >
                          {totalValue !== undefined ? totalValue : "\u00A0"}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, i) => (
                <tr
                  key={i}
                >
                  {columns.map((col) => {
                    let value = row[col];
                    const isNumeric = numericColumns.has(col);
                    const displayLabel = displayNameMap[col] || col.replace(/_/g, " ");
                    const isSlNo = isSlNoColumn(displayLabel, col);

                    if (col.toLowerCase().includes("date")) {
                      value = formatDate(value);
                    } else if (typeof value === "number") {
                      value = formatNumber(value);
                    } else if (value === null || value === undefined) {
                      value = "-";
                    }

                    return (
                      <td
                        key={col}
                        className={`${shouldWrapContent(displayLabel) ? "whitespace-normal break-words" : "whitespace-nowrap"} ${isSlNo ? "text-center" : isNumeric ? "text-right" : "text-left"
                          }`}
                        style={{ padding: "4px 6px", ...getColumnWidthStyle(displayLabel) }}
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  // Dynamic tabs based on type
  const getTabsByType = () => {
    const baseType = type ? type.toLowerCase() : "";

    if (baseType.includes("summary")) {
      return [
        { id: "moowr", label: "MOOWR Purchase", icon: "file" },
        { id: "rmBoe", label: "RM By BOE", icon: "archive" },
        { id: "wip", label: "WIP by BOE", icon: "settings" },
        { id: "scrap", label: "Scrap by BOE", icon: "refresh" },
        { id: "sales", label: "Sales by BOE No", icon: "chart" },



      ];
    } else if (baseType.includes("moowr")) {
      return [
        { id: "summary", label: "MOOWR Purchase", icon: "file" },
        { id: "details", label: "Additional Details", icon: "chart" },
      ];
    } else if (baseType.includes("sales")) {
      return [
        { id: "summary", label: "Sales by BOE No", icon: "chart" },
        { id: "details", label: "Details", icon: "file" },
      ];
    } else if (baseType.includes("scrap")) {
      return [
        { id: "summary", label: "Scrap by BOE", icon: "refresh" },
        { id: "details", label: "Details", icon: "file" },
      ];
    } else if (baseType.includes("wip")) {
      return [
        { id: "summary", label: "WIP by BOE", icon: "settings" },
        { id: "details", label: "Details", icon: "file" },
      ];
    }

    return [{ id: "summary", label: "Summary", icon: "file" }];
  };

  const tabs = getTabsByType();
  const renderTabIcon = (iconName) => {
    if (iconName === "chart") {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12v6m5-10v10m5-14v14M4 20h16" />
        </svg>
      );
    }
    if (iconName === "refresh") {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v6h6M20 20v-6h-6M20 10a8 8 0 00-14-4M4 14a8 8 0 0014 4" />
        </svg>
      );
    }
    if (iconName === "settings") {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8.5A3.5 3.5 0 1012 15.5 3.5 3.5 0 0012 8.5zm8 3.5l-1.7.9c.04.35.04.71 0 1.06l1.7.9-1.5 2.6-1.7-.9a6.45 6.45 0 01-.92.53l-.2 1.92h-3l-.2-1.92a6.45 6.45 0 01-.92-.53l-1.7.9-1.5-2.6 1.7-.9a4.7 4.7 0 010-1.06l-1.7-.9 1.5-2.6 1.7.9c.3-.2.61-.37.92-.53l.2-1.92h3l.2 1.92c.31.16.62.33.92.53l1.7-.9L20 12z" />
        </svg>
      );
    }
    if (iconName === "archive") {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M5 7l1 12h12l1-12M9 11h6" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 3h6l5 5v13H5V3h3zm6 1.5V9h4.5" />
      </svg>
    );
  };
  const isSummaryType = (type || "").toLowerCase().includes("summary");
  const hasSummaryTabData = Object.values(summaryTabData).some((tabData) => {
    if (!tabData) return false;
    return (tabData.Table && tabData.Table.length > 0) ||
      (tabData.Table1 && tabData.Table1.length > 0) ||
      (tabData.Table2 && tabData.Table2.length > 0) ||
      (tabData.Table3 && tabData.Table3.length > 0);
  });
  const hasDisplayData = isSummaryType ? hasSummaryTabData : !!data;
  const summaryMainRecord = summaryTabData?.summary?.Table?.[0];

  // Render tab content based on type
  const renderTabContent = () => {
    const baseType = type ? type.toLowerCase() : "";

    if (baseType.includes("summary")) {
      const currentTabData = summaryTabData[activeTab];
      const currentConfig = isColumnConfigTable(currentTabData?.Table1) ? currentTabData.Table1 : null;
      const currentColumns = currentTabData?.Table?.length ? getOrderedColumns(currentTabData.Table, currentConfig) : [];
      const currentTotals = isTotalsTable(currentTabData?.Table2, currentColumns) ? currentTabData.Table2 : null;
      return (
        <>
          {activeTab === "sales" && currentTabData?.Table && renderTable(currentTabData.Table, "Sales by BOE No", "summary-sales", currentConfig, { totalsTable: currentTotals })}
          {activeTab === "scrap" && currentTabData?.Table && renderTable(currentTabData.Table, "Scrap by BOE", "summary-scrap", currentConfig, { totalsTable: currentTotals })}
          {activeTab === "wip" && currentTabData?.Table && renderTable(currentTabData.Table, "WIP by BOE", "summary-wip", currentConfig, { totalsTable: currentTotals })}
          {activeTab === "moowr" && currentTabData?.Table && renderTable(currentTabData.Table, "MOOWR Purchase", "summary-moowr", currentConfig, { totalsTable: currentTotals })}
          {activeTab === "rmBoe" && currentTabData?.Table && renderTable(currentTabData.Table, "RM By BOE", "summary-rmBoe", currentConfig, { totalsTable: currentTotals })}

          {activeTab === "sales" && !currentTabData?.Table && (
            <div className="bg-white rounded-lg shadow p-4 text-center"><p className="text-gray-500">No sales data available</p></div>
          )}
          {activeTab === "scrap" && !currentTabData?.Table && (
            <div className="bg-white rounded-lg shadow p-4 text-center"><p className="text-gray-500">No scrap data available</p></div>
          )}
          {activeTab === "wip" && !currentTabData?.Table && (
            <div className="bg-white rounded-lg shadow p-4 text-center"><p className="text-gray-500">No WIP data available</p></div>
          )}
          {activeTab === "moowr" && !currentTabData?.Table && (
            <div className="bg-white rounded-lg shadow p-4 text-center"><p className="text-gray-500">No MOOWR data available</p></div>
          )}
          {activeTab === "rmBoe" && !currentTabData?.Table && (
            <div className="bg-white rounded-lg shadow p-4 text-center"><p className="text-gray-500">No RM By BOE data available</p></div>
          )}
        </>
      );
    } else if (baseType.includes("moowr")) {
      const mainConfig = isColumnConfigTable(data?.Table1) ? data.Table1 : null;
      const mainColumns = data?.Table?.length ? getOrderedColumns(data.Table, mainConfig) : [];
      const mainTotals = isTotalsTable(data?.Table2, mainColumns) ? data.Table2 : null;
      return (
        <>
          {activeTab === "summary" && data?.Table && (
            <div>
              {data.Table.length > 0 && (
                <>
                  {renderRecord(data.Table[0], "MOOWR Purchase - Main Details")}
                  {renderTable(data.Table, "MOOWR Purchase Records", "moowr-main", mainConfig, { totalsTable: mainTotals })}
                </>
              )}
            </div>
          )}

          {activeTab === "details" && (
            <div className="space-y-4">
              {data?.Table1 && !isColumnConfigTable(data.Table1) && renderTable(data.Table1, "Additional Details - Table 1", "moowr-table1")}
              {data?.Table2 && !mainTotals && renderTable(data.Table2, "Additional Details - Table 2", "moowr-table2")}
              {data?.Table3 && renderTable(data.Table3, "Additional Details - Table 3", "moowr-table3")}
              {!data?.Table1 && !(data?.Table2 && !mainTotals) && !data?.Table3 && (
                <div className="bg-white rounded-lg shadow p-4 text-center">
                  <p className="text-gray-500">No additional details available</p>
                </div>
              )}
            </div>
          )}
        </>
      );
    } else if (baseType.includes("sales")) {
      const mainConfig = isColumnConfigTable(data?.Table1) ? data.Table1 : null;
      const mainColumns = data?.Table?.length ? getOrderedColumns(data.Table, mainConfig) : [];
      const mainTotals = isTotalsTable(data?.Table2, mainColumns) ? data.Table2 : null;
      return (
        <>
          {activeTab === "summary" && data?.Table && (
            <div>
              {data.Table.length > 0 && renderTable(data.Table, "Sales by BOE No", "sales-main", mainConfig, { totalsTable: mainTotals })}
            </div>
          )}

          {activeTab === "details" && (
            <div className="space-y-4">
              {data?.Table1 && !isColumnConfigTable(data.Table1) && renderTable(data.Table1, "Related Details", "sales-related")}
              {data?.Table2 && !mainTotals && renderTable(data.Table2, "Additional Info", "sales-additional")}
              {!data?.Table1 && !(data?.Table2 && !mainTotals) && (
                <div className="bg-white rounded-lg shadow p-4 text-center">
                  <p className="text-gray-500">No additional details available</p>
                </div>
              )}
            </div>
          )}
        </>
      );
    } else if (baseType.includes("scrap")) {
      const mainConfig = isColumnConfigTable(data?.Table1) ? data.Table1 : null;
      const mainColumns = data?.Table?.length ? getOrderedColumns(data.Table, mainConfig) : [];
      const mainTotals = isTotalsTable(data?.Table2, mainColumns) ? data.Table2 : null;
      return (
        <>
          {activeTab === "summary" && data?.Table && (
            <div>
              {data.Table.length > 0 && renderTable(data.Table, "Scrap by BOE", "scrap-main", mainConfig, { totalsTable: mainTotals })}
            </div>
          )}

          {activeTab === "details" && (
            <div className="space-y-4">
              {data?.Table1 && !isColumnConfigTable(data.Table1) && renderTable(data.Table1, "Related Details", "scrap-related")}
              {data?.Table2 && !mainTotals && renderTable(data.Table2, "Additional Info", "scrap-additional")}
              {!data?.Table1 && !(data?.Table2 && !mainTotals) && (
                <div className="bg-white rounded-lg shadow p-4 text-center">
                  <p className="text-gray-500">No additional details available</p>
                </div>
              )}
            </div>
          )}
        </>
      );
    } else if (baseType.includes("wip")) {
      const mainConfig = isColumnConfigTable(data?.Table1) ? data.Table1 : null;
      const mainColumns = data?.Table?.length ? getOrderedColumns(data.Table, mainConfig) : [];
      const mainTotals = isTotalsTable(data?.Table2, mainColumns) ? data.Table2 : null;
      return (
        <>
          {activeTab === "summary" && data?.Table && (
            <div>
              {data.Table.length > 0 && renderTable(data.Table, "WIP by BOE", "wip-main", mainConfig, { totalsTable: mainTotals })}
            </div>
          )}

          {activeTab === "details" && (
            <div className="space-y-4">
              {data?.Table1 && !isColumnConfigTable(data.Table1) && renderTable(data.Table1, "Related Details", "wip-related")}
              {data?.Table2 && !mainTotals && renderTable(data.Table2, "Additional Info", "wip-additional")}
              {!data?.Table1 && !(data?.Table2 && !mainTotals) && (
                <div className="bg-white rounded-lg shadow p-4 text-center">
                  <p className="text-gray-500">No additional details available</p>
                </div>
              )}
            </div>
          )}
        </>
      );
    }

    // Default rendering
    const defaultConfig = isColumnConfigTable(data?.Table1) ? data.Table1 : null;
    const defaultColumns = data?.Table?.length ? getOrderedColumns(data.Table, defaultConfig) : [];
    const defaultTotals = isTotalsTable(data?.Table2, defaultColumns) ? data.Table2 : null;
    return (
      <>
        {activeTab === "summary" && data?.Table && renderTable(
          data.Table,
          "Summary",
          "default-summary",
          defaultConfig,
          { totalsTable: defaultTotals }
        )}
      </>
    );
  };

  return (
    <div className="space-y-1 h-full flex flex-col min-h-0">
      <style>{`
        .tab-content { animation: fadeIn 0.3s ease-in; }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      {/* Summary Rev Main Details (Top, all tabs) */}
      {isSummaryType && summaryMainRecord && (
        renderSummaryHighlights(summaryMainRecord)
      )}

      {/* Tab Navigation */}
      <div className="detail-tabs no-print">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`detail-tab ${activeTab === tab.id ? "is-active" : ""}`}
          >
            <span className="mr-2 inline-flex items-center">{renderTabIcon(tab.icon)}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="detail-loading">
          <div className="summary-spinner"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="detail-error">
          <p>{error}</p>
          <button
            onClick={() => {
              if (type?.toLowerCase().includes("summary")) {
                fetchSummaryTabData(activeTab);
              } else {
                fetchBoeData(type);
              }
            }}
            className="summary-btn"
          >
            Retry
          </button>
        </div>
      )}

      {/* Tab Content */}
      {!loading && !error && hasDisplayData && (
        <div className="tab-content min-h-0 flex-1 overflow-auto detail-section">
          {renderTabContent()}
        </div>
      )}

      {/* No Data State */}
      {!loading && !error && !hasDisplayData && (
        <div className="detail-empty">
          <p>No data to display</p>
        </div>
      )}

      {fullscreenTable && (
        <div className="detail-page detail-overlay">
          <div className="detail-header">
            <h2 className="detail-header-title">{fullscreenTable.title}</h2>
            <button
              type="button"
              onClick={() => setFullscreenTable(null)}
              className="summary-btn"
              title="Minimize"
            >
              Close
            </button>
          </div>
          <div className="detail-content">
            {renderTable(
              fullscreenTable.tableData,
              fullscreenTable.title,
              fullscreenTable.key,
              fullscreenTable.columnConfig || null,
              {
                showHeader: false,
                allowFullscreen: false,
                isFullscreen: true,
                totalsTable: fullscreenTable.totalsTable || null,
              }
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BoeDetailView;
